import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { getTenant, getTenantName } from '../shared/tenant.util';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private token: string | null = null;

  constructor(private authService: AuthService, private router: Router) {
    //  Subscribe to token updates globally
    this.authService.token$.subscribe(token => {
      this.token = token;
    });
  }

  /**
   * Handles server errors (5xx) and network errors (status 0) by redirecting
   * to the server-error page, unless already there.
   */
  private handleServerError(error: HttpErrorResponse): void {
    if ((error.status >= 500 || error.status === 0) && !this.router.url.startsWith('/server-error')) {
      this.router.navigate(['/server-error']);
    }
  }

  /**
   * Creates a catchError operator that handles 5xx/network errors consistently.
   */
  private createServerErrorHandler(): MonoTypeOperatorFunction<HttpEvent<any>> {
    return catchError((error: HttpErrorResponse) => {
      this.handleServerError(error);
      return throwError(() => error);
    });
  }

  /**
   * Marks the request as coming from an SM front-end (SM.API: ApiClientGateMiddleware).
   *
   * The SM.Auth cookie is ambient — the browser attaches it to anything aimed at the API host,
   * including a URL typed into the address bar, which is how a direct hit on another tenant's
   * API could silently federate and mint a second session. This header is NOT a secret (it ships
   * in this bundle); it works because a browser never sends it on its own, so the backend can
   * tell our XHR apart from a navigation.
   *
   * Applied to every branch below by cloning first, so the WS-Fed path itself is unchanged.
   * Scoped to the tenant backend so the key is never sent to any other host.
   */
  private withClientKey(req: HttpRequest<any>): HttpRequest<any> {
    const apiBase = getTenant();
    if (!apiBase || !req.url.startsWith(apiBase)) {
      return req;
    }
    return req.clone({
      setHeaders: {
        'X-SM-Client-Key': environment.smClientKey,
        // The key is common to every tenant because this bundle is. This states WHICH tenant
        // we resolved, and the backend refuses it unless it matches its own - so a tab that has
        // resolved the wrong backend is rejected rather than served another tenant's data.
        'X-SM-Tenant': getTenantName()
      }
    });
  }

  intercept(originalReq: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const req = this.withClientKey(originalReq);

    // Allow requests marked with 'skip-auth-intercept' to bypass token handling.
    // This header is used by callers that need unauthenticated or custom auth flows.
    if (req.headers.has('skip-auth-intercept')) {
      const cleanReq = req.clone({
        headers: req.headers.delete('skip-auth-intercept')
      });
      return next.handle(cleanReq).pipe(this.createServerErrorHandler());
    }

    // MAGBypass (non-cutover tenants): backend fakes the identity from Azure config.
    // Do NOT send credentials and do NOT redirect to WS-Fed on error — these tenants
    // have no MAG login in the new flow. Restores the pre-WS-Fed MAGBypass behavior.
    if (this.token === 'MAG_BYPASS_ENABLED') {
      const isFormData = req.body instanceof FormData;
      const clonedReq = req.clone({
        setHeaders: {
          'accept': '*/*',
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        }
      });
      return next.handle(clonedReq).pipe(this.createServerErrorHandler());
    }

    // WS-Federation session (cutover tenants, e.g. sem1): send cookies; on 401
    // redirect to WS-Fed login. DO NOT MODIFY — this is the working sem1 path.
    if (this.token === 'WS_FED_SESSION') {
      const isFormData = req.body instanceof FormData;
      const clonedReq = req.clone({
        setHeaders: {
          'accept': '*/*',
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        },
        withCredentials: true  // Important: send cookies for WS-Fed authentication
      });
      return next.handle(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Session expired - redirect to WS-Fed login
            console.log('[AuthInterceptor] 401 received - redirecting to WS-Fed login');
            const tenant = getTenantName();
            const backendBaseUrl = getTenant()?.replace(/\/API$/i, '');
            const returnUrl = encodeURIComponent(window.location.href);
            // Guard: only redirect when a backend base URL actually resolved —
            // otherwise a null getTenant() would build a broken "undefined/auth/startlogin".
            if (backendBaseUrl) {
              window.location.href = `${backendBaseUrl}/auth/startlogin?tenant=${encodeURIComponent(tenant)}&returnUrl=${returnUrl}`;
            }
          }
this.handleServerError(error); return throwError(() => error);
        })
      );
    }

    // Legacy token-based auth (keep for backward compatibility)
    if (this.token) {
      const isFormData = req.body instanceof FormData;
  
      const clonedReq = req.clone({
        setHeaders: {
          'accept': '*/*',
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          Authorization: `Bearer ${this.token}`
        }
      });
  
      return next.handle(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // ========== Legacy Email Prompt (Commented for reference) ==========
            // const username = prompt('Session expired. Please enter your email address to fetch a new token:');
            // if (username) {
            //   return from(this.authService.fetchTokenFromAPI(username)).pipe(
            //     switchMap(() => {
            //       const newToken = this.authService.getToken();
            //       const retryReq = req.clone({
            //         setHeaders: {
            //           'accept': '*/*',
            //           ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            //           Authorization: `Bearer ${newToken}`
            //         }
            //       });
            //       return next.handle(retryReq);
            //     }),
            //     catchError(tokenError => {
            //       console.error('Failed to fetch new token:', tokenError);
            //       return throwError(() => tokenError);
            //     })
            //   );
            // }
            // ===================================================================
            
            // Session expired - redirect to WS-Fed login
            console.log('[AuthInterceptor] 401 with token - redirecting to WS-Fed login');
            const tenant = getTenantName();
            const backendBaseUrl = getTenant()?.replace(/\/API$/i, '');
            const returnUrl = encodeURIComponent(window.location.href);
            // Guard: only redirect when a backend base URL actually resolved —
            // otherwise a null getTenant() would build a broken "undefined/auth/startlogin".
            if (backendBaseUrl) {
              window.location.href = `${backendBaseUrl}/auth/startlogin?tenant=${encodeURIComponent(tenant)}&returnUrl=${returnUrl}`;
            }
          }

          // Handle 5xx/network errors
          this.handleServerError(error);
          return throwError(() => error);
        })
      );
    }
  
    return next.handle(req).pipe(this.createServerErrorHandler());
  }
  
}
